import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BaseError } from 'libs/end-to-end/src/types/errors/base-error';
import { CreateOrUpdateOrganizationRequestBodySchema } from 'libs/end-to-end/src/types/organizations/organization';
import { Organization } from 'libs/end-to-end/src/types/organizations/organization';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { TouchTargetBehavior } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { SaveIndicatorStatus } from 'apps/sigma2-ui/src/components/SaveIndicator';
import { useSimpleWritePattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleWritePattern';
import { useOrganizationLogoUpload } from 'apps/sigma2-ui/src/organizations/lib/useOrganizationLogoUpload';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { LogoFileMimeType } from 'libs/end-to-end/src/logos';

/**
 * Interface representing the return values of the useOrganizationNewPage hook
 * @interface OrganizationNewPageHook
 */
export interface OrganizationNewPageHook {
  /** Label for the submit button */
  submitButtonLabel: React.ReactNode;
  /** Current status of the save indicator */
  saveIndicatorStatus: SaveIndicatorStatus;
  /** Behavior configuration for the submit button */
  submitButtonBehavior: TouchTargetBehavior;
  /** Whether the submit button should be disabled */
  submitButtonDisabled: boolean;

  /** Error object if the operation failed */
  error: BaseError | null;
  /** Whether the organization was successfully saved */
  saved: boolean;

  /** Function to create a new organization when the name and image are set and the submit button is clicked */
  handleOrganizationCreate: (name: string, imageBlob: Blob | null) => void;

  /** Organization name */
  name: string;
  /** Function to update organization name */
  setName: (name: string) => void;
  /** Selected file URL for the logo */
  selectedFile: string | null;

  /** Whether to show the image cropper */
  showCropper: boolean;
  /** Whether a file is being dragged over the drop zone */
  dragActive: boolean;
  /** URL of the uploaded and cropped image */
  uploadedImageUrl: string | null;
  /** Whether an upload is in progress */
  isUploading: boolean;
  /** Blob of the cropped image */
  croppedBlob: Blob | null;
  /** Handler for file input changes */
  handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Handler for drag events */
  handleDrag: (e: React.DragEvent) => void;
  /** Handler for drop events */
  handleDrop: (e: React.DragEvent) => void;
  /** Handler for when image cropping is complete */
  handleCropComplete: (blob: Blob) => void;
  /** Handler to remove the uploaded image */
  handleRemoveImage: () => void;
  /** Function to toggle the cropper visibility */
  setShowCropper: (show: boolean) => void;
  /** Function to set the selected file URL */
  setSelectedFile: (file: string | null) => void;
}

/**
 * Hook for managing the organization creation page
 * Handles organization creation, logo upload, and image processing
 *
 * @returns {OrganizationNewPageHook} Object containing state and handlers for organization creation
 *
 */
export const useOrganizationNewPage = (): OrganizationNewPageHook => {
  // State management
  const [name, setName] = useState('');
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [croppedBlob, setCroppedBlob] = useState<Blob | null>(null);

  // API management patterns
  const patternGetOrganizationsUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations`;
  }, []);

  // Write pattern to create a new organization. After it is created, the logo hook is triggered to upload the image.
  const organizationsWritePattern = useSimpleWritePattern({
    getUrlFn: patternGetOrganizationsUrlFn,
    canWrite: true,
    writeSchema: CreateOrUpdateOrganizationRequestBodySchema,
    responseSchema: Organization,
    method: 'POST',
  });

  // Navigation and logo upload hooks
  const navigate = useNavigateSafe();
  const uploadImageHook = useOrganizationLogoUpload();

  // Function to upload the image after the organization is created. When the upload is complete, the user is navigated to the organization edit page.
  const handleUploadImage = useCallback(
    (createdOrganization: Organization) => {
      if (croppedBlob) {
        const validatedMimeType = LogoFileMimeType.safeParse(croppedBlob.type);
        if (validatedMimeType.success) {
          const organization = {
            ...createdOrganization,
            logoMimeType: validatedMimeType.data,
          };
          uploadImageHook.uploadLogoImage(organization, croppedBlob, () => {
            navigate(`/organizations/edit/${organization.id}`);
          });
          return;
        }
      }

      navigate(`/organizations/edit/${createdOrganization.id}`);
    },
    [croppedBlob, uploadImageHook, navigate],
  );

  /*
   * Function that triggers the creation of a new organization.
   * It also triggers the upload of the image after the organization is created calling the handleUploadImage function.
   */
  const handleOrganizationCreate = useCallback(
    (name: string) => {
      if (organizationsWritePattern.status === 'saving' || name.length <= 0) {
        return;
      }

      let validatedMimeType: LogoFileMimeType | undefined;
      if (croppedBlob) {
        const validated = LogoFileMimeType.safeParse(croppedBlob.type);
        if (!validated.success) {
          return;
        }

        validatedMimeType = validated.data;
      }

      organizationsWritePattern.saveEntity(
        {
          name,
          logoMimeType: validatedMimeType,
          logoUrl: crypto.randomUUID().substring(0, 8) + '-logo',
        },
        handleUploadImage,
      );
    },
    [organizationsWritePattern, handleUploadImage, croppedBlob],
  );

  // File validation
  const validateFile = useCallback((file: File) => {
    const validatedMimeType = LogoFileMimeType.safeParse(file.type);
    if (!validatedMimeType.success) {
      alert('Please upload an image file (JPG, PNG, WebP, SVG)');
      return false;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('File size should be less than 5MB');
      return false;
    }
    return true;
  }, []);

  // File handling
  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files[0]) {
        const file = event.target.files[0];
        if (validateFile(file)) {
          setSelectedFile(URL.createObjectURL(file));
          setShowCropper(true);
        }
      }
    },
    [validateFile],
  );

  // Drag and drop handling
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  // Function to handle the drop event
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        const file = e.dataTransfer.files[0];
        if (validateFile(file)) {
          setSelectedFile(URL.createObjectURL(file));
          setShowCropper(true);
        }
      }
    },
    [validateFile],
  );

  // Function to handle the crop complete event
  const handleCropComplete = useCallback((blob: Blob) => {
    setCroppedBlob(blob);
    setUploadedImageUrl(URL.createObjectURL(blob));
    setShowCropper(false);
  }, []);

  // Function to handle the remove image event
  const handleRemoveImage = useCallback(() => {
    if (uploadedImageUrl) {
      URL.revokeObjectURL(uploadedImageUrl);
    }
    setUploadedImageUrl(null);
    setSelectedFile(null);
    setCroppedBlob(null);
  }, [uploadedImageUrl]);

  // Cleanup effect to revoke the object URL when the uploaded image is removed
  useEffect(() => {
    return () => {
      if (uploadedImageUrl) {
        URL.revokeObjectURL(uploadedImageUrl);
      }
    };
  }, [uploadedImageUrl]);

  // Function to handle the submit event
  const handleSubmit = useCallback(() => {
    if (name.length <= 0) return;
    setIsUploading(true);
    try {
      handleOrganizationCreate(name);
    } finally {
      setIsUploading(false);
    }
  }, [name, handleOrganizationCreate]);

  // Memoized hook return value
  return useMemo(
    () => ({
      saved:
        organizationsWritePattern.status === 'done' &&
        organizationsWritePattern.saved,
      error:
        organizationsWritePattern.status === 'error'
          ? organizationsWritePattern.error
          : null,
      submitButtonDisabled:
        !organizationsWritePattern.canWrite ||
        !name.trim() ||
        isUploading ||
        showCropper,
      submitButtonLabel: 'Create',
      saveIndicatorStatus: organizationsWritePattern.saveIndicatorStatus,
      submitButtonBehavior: {
        type: 'onClick' as const,
        onClick: handleSubmit,
      },
      handleOrganizationCreate,
      name,
      setName,
      selectedFile,
      showCropper,
      dragActive,
      uploadedImageUrl,
      isUploading,
      croppedBlob,
      handleFileSelect,
      handleDrag,
      handleDrop,
      handleCropComplete,
      handleRemoveImage,
      setShowCropper,
      setSelectedFile,
    }),
    [
      organizationsWritePattern,
      handleOrganizationCreate,
      name,
      selectedFile,
      showCropper,
      dragActive,
      uploadedImageUrl,
      isUploading,
      croppedBlob,
      handleFileSelect,
      handleDrag,
      handleDrop,
      handleCropComplete,
      handleRemoveImage,
      setShowCropper,
      setSelectedFile,
      handleSubmit,
    ],
  );
};
